import { useEffect, useMemo, useState } from 'react';
import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useUpdateEffect } from 'ahooks';
import { SortBy } from 'tea-component/lib/table/addons';
import { getUgcTrendRelatedVideos } from '@opm/services';
import { useStableValue } from '@opm/utils/stableKey';
import { DEFAULT_PAGE_INFO, DRAWER_RELATED_VIDEOS_COUNT } from '../constants';
import { toVideoCardItem } from '../types';

/**
 * 直接复用 `Videos/VideoTable`（VideoTableList）的 sort_item 命名空间，
 * 仅在发请求时把 Date 列的 key 翻译到 UGC related_videos API 期望的 `comment_time`。
 *
 *   组件/前端 state: `video_release_time`（与 Videos 主页面一致）
 *   后端 sort_item:  `comment_time`
 *
 * 其它 sort key（tweets_view / tweets_like / engagement）前后端一致，无需翻译。
 *
 * 列表接口和下载接口共用同一份 sort_item 约定，下载侧也用同一个翻译器。
 */
export function toBackendSortItem(by: string | undefined): string | undefined {
  if (by === 'video_release_time') return 'comment_time';
  return by;
}

export function useDrawerRelatedVideos({
  filter,
  sortItem,
  sortModel = 'desc',
}: {
  filter: any;
  sortItem?: string;
  sortModel?: 'asc' | 'desc';
}) {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: () => {
      setList([]);
      setTotal(0);
    },
  });
  const stableFilter = useStableValue(filter);

  const params = useMemo(() => {
    if (!stableFilter?.game_id || !stableFilter?.topic) return null;
    return {
      ...stableFilter,
      page: 1,
      page_size: DRAWER_RELATED_VIDEOS_COUNT,
      sort_item: toBackendSortItem(sortItem) || 'tweets_view',
      sort_model: sortModel,
    };
  }, [stableFilter, sortItem, sortModel]);

  useEffect(() => {
    if (!params) return;
    wrapperCancelRequest(getUgcTrendRelatedVideos, params)
      .then((response: any) => {
        setList((response?.list || []).map(toVideoCardItem));
        setTotal(response?.total || 0);
      })
      .catch(() => {});
  }, [params]);

  return { list, total, loading };
}

export function useUgcRelatedVideos({
  filter,
  defaultPageInfo = DEFAULT_PAGE_INFO,
  defaultViewMode = 'card',
}: {
  filter: any;
  defaultPageInfo?: { page: number; page_size: number };
  defaultViewMode?: 'card' | 'table';
}) {
  const [viewMode, setViewMode] = useState<'card' | 'table'>(defaultViewMode);
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: 'tweets_view',
  });
  const { pageInfo, changePageInfo, resetPageInfo, nextPage } = usePageInfo({
    defaultPageInfo,
  });

  const [cardDataRef, cardRecords, setCardData] = useStateRef<any[]>([]);
  const [tableRecords, setTableRecords] = useState<any[]>([]);
  const [total, setTotal] = useState(0);

  const { wrapperCancelRequest, loading, loadingRef } = useCancelRequestHook({
    clear: () => {
      setTableRecords([]);
      setTotal(0);
    },
  });

  function resetAllData() {
    setCardData([]);
    setTableRecords([]);
    setTotal(0);
    resetPageInfo();
  }

  function changeSortBy(newSortBy: SortBy<string>) {
    setSortBy(newSortBy);
    resetAllData();
  }

  function changeKeyword(val: string) {
    if (val === keywordRef.current) return;
    setKeyword(val);
    resetAllData();
  }

  function changeViewMode(mode: 'card' | 'table') {
    if (mode === viewMode) return;
    setViewMode(mode);
  }

  function loadNextPage() {
    if (loadingRef.current || viewMode !== 'card') return;
    const loadedCount = cardDataRef.current?.length || 0;
    if (loadedCount >= total && total > 0) return;
    nextPage();
  }

  function changeTablePageInfo(newPageInfo?: {
    page: number;
    page_size: number;
  }) {
    setTableRecords([]);
    changePageInfo(newPageInfo);
  }

  const stableFilter = useStableValue(filter);
  const stablePageInfo = useStableValue(pageInfo);

  useUpdateEffect(() => {
    if (!stableFilter) return;
    resetAllData();
  }, [stableFilter]);

  const params = useMemo(() => {
    if (!stableFilter?.game_id || !stableFilter?.topic) return null;
    return {
      ...stableFilter,
      ...stablePageInfo,
      sort_item: toBackendSortItem(sortByRef.current?.by),
      sort_model: sortByRef.current?.order,
      search_text: keywordRef.current,
    };
  }, [stableFilter, stablePageInfo, sortBy, keyword]);

  function request() {
    if (!params) return;
    wrapperCancelRequest(getUgcTrendRelatedVideos, params)
      .then((response: any) => {
        const { list = [], total: totalCount = 0 } = response || {};
        const realList = list.map(toVideoCardItem);

        if (viewMode === 'card') {
          if (pageInfo.page === 1) {
            setCardData(realList);
          } else {
            setCardData([...(cardDataRef.current || []), ...realList]);
          }
        } else {
          setTableRecords(realList);
        }

        setTotal(totalCount);
      })
      .catch(() => {
        // 请求失败 / 被取消：useCancelRequestHook.clear 已重置 list/total，
        // 分页态保持不变，等用户后续操作再触发新的请求。
      });
  }

  useEffect(() => {
    if (!params) return;
    request();
  }, [params, viewMode]);

  const currentRecords = viewMode === 'card' ? cardRecords || [] : tableRecords;
  const isEmpty = !loading && currentRecords.length === 0;

  return {
    cardRecords: cardRecords || [],
    tableRecords,
    total,
    pageInfo,
    changeTablePageInfo,
    loadNextPage,
    loading,
    isFirstLoading:
      loading && pageInfo.page === 1 && currentRecords.length === 0,
    isEmpty,
    isPaginationLoading:
      loading && viewMode === 'card' && (cardRecords?.length || 0) > 0,
    sortBy: sortBy as SortBy<string>,
    setSortBy: changeSortBy,
    keyword: keyword || '',
    setKeyword: changeKeyword,
    viewMode,
    setViewMode: changeViewMode,
  };
}
